var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Chip, } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Download as DownloadIcon, Refresh as RefreshIcon, Warning as WarningIcon } from '@mui/icons-material';
import { serviceHistoryService } from '@services/serviceHistoryService';
import { formatDate, formatDateForFilename } from '@utils/dateUtils';
import { machineService } from '@services/machineService';
import { businessService } from '@services/businessService';
const AdminServiceHistory = () => {
    const [serviceHistory, setServiceHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    useEffect(() => {
        loadServiceHistory();
    }, []);
    const loadServiceHistory = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            setError(null);
            // Load all businesses, machines, and service history
            const businesses = yield businessService.getAll();
            const allServiceHistory = [];
            for (const business of businesses) {
                const machines = yield machineService.getByBusinessId(business.id);
                for (const machine of machines) {
                    const history = yield serviceHistoryService.getByMachineId(machine.id);
                    const historyWithDetails = history.map((item) => {
                        let daysUntilNextService;
                        let isOverdue = false;
                        if (item.nextServiceDate) {
                            const nextDate = new Date(item.nextServiceDate);
                            const today = new Date();
                            daysUntilNextService = Math.ceil((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                            isOverdue = daysUntilNextService < 0;
                        }
                        return Object.assign(Object.assign({}, item), { machineName: machine.name, businessId: business.id, businessName: business.name, daysUntilNextService,
                            isOverdue });
                    });
                    allServiceHistory.push(...historyWithDetails);
                }
            }
            // Sort by next service date (overdue first, then by date)
            allServiceHistory.sort((a, b) => {
                if (a.isOverdue && !b.isOverdue)
                    return -1;
                if (!a.isOverdue && b.isOverdue)
                    return 1;
                if (a.nextServiceDate && b.nextServiceDate) {
                    return new Date(a.nextServiceDate).getTime() - new Date(b.nextServiceDate).getTime();
                }
                if (a.nextServiceDate)
                    return -1;
                if (b.nextServiceDate)
                    return 1;
                return new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime();
            });
            setServiceHistory(allServiceHistory);
        }
        catch (err) {
            console.error('Error loading service history:', err);
            setError(err.message || 'Failed to load service history');
        }
        finally {
            setLoading(false);
        }
    });
    const handleExport = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setExporting(true);
            setError(null);
            const blob = yield serviceHistoryService.exportToExcel();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `service_history_export_${formatDateForFilename()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }
        catch (err) {
            console.error('Error exporting service history:', err);
            setError(err.message || 'Failed to export service history');
        }
        finally {
            setExporting(false);
        }
    });
    const columns = [
        {
            field: 'businessName',
            headerName: 'Business',
            width: 150,
            renderCell: (params) => (_jsx(Chip, { label: params.value, color: "primary", variant: "outlined", size: "small" })),
        },
        {
            field: 'machineName',
            headerName: 'Machine',
            width: 180,
            renderCell: (params) => (_jsx(Typography, { variant: "body2", fontWeight: "bold", children: params.value })),
        },
        {
            field: 'serviceDate',
            headerName: 'Service Date',
            width: 130,
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: 'nextServiceDate',
            headerName: 'Next Service',
            width: 130,
            renderCell: (params) => {
                if (!params.value)
                    return '—';
                const dateStr = formatDate(params.value);
                const row = params.row;
                if (row.isOverdue) {
                    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(WarningIcon, { color: "error", fontSize: "small" }), _jsx(Typography, { variant: "body2", color: "error", children: dateStr })] }));
                }
                if (row.daysUntilNextService !== undefined && row.daysUntilNextService <= 30) {
                    return (_jsx(Typography, { variant: "body2", color: "warning.main", children: dateStr }));
                }
                return dateStr;
            },
        },
        {
            field: 'daysUntilNextService',
            headerName: 'Days Until',
            width: 110,
            renderCell: (params) => {
                if (params.value === undefined)
                    return '—';
                if (params.value < 0) {
                    return (_jsx(Chip, { label: `${Math.abs(params.value)} overdue`, color: "error", size: "small" }));
                }
                if (params.value <= 7) {
                    return _jsx(Chip, { label: params.value, color: "error", size: "small" });
                }
                if (params.value <= 30) {
                    return _jsx(Chip, { label: params.value, color: "warning", size: "small" });
                }
                return _jsx(Chip, { label: params.value, color: "success", size: "small" });
            },
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 250,
        },
        {
            field: 'partsUsed',
            headerName: 'Parts Used',
            width: 250,
            renderCell: (params) => {
                const parts = params.value;
                if (!parts || parts.length === 0)
                    return '—';
                const partsText = parts
                    .map(p => `${p.itemName} (${p.itemsUsed})`)
                    .join(', ');
                return (_jsx(Typography, { variant: "body2", sx: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }, title: partsText, children: partsText }));
            },
        },
        {
            field: 'performedBy',
            headerName: 'Performed By',
            width: 150,
        },
        {
            field: 'cost',
            headerName: 'Cost',
            width: 120,
            valueFormatter: (value) => value ? `$${value.toFixed(2)}` : '—',
        },
        {
            field: 'notes',
            headerName: 'Notes',
            width: 200,
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 130,
            valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        },
    ];
    const overdueCount = serviceHistory.filter(s => s.isOverdue).length;
    const upcomingCount = serviceHistory.filter(s => s.daysUntilNextService !== undefined && s.daysUntilNextService > 0 && s.daysUntilNextService <= 30).length;
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }, children: [_jsx(Typography, { variant: "h6", children: "All Service History" }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(RefreshIcon, {}), onClick: loadServiceHistory, disabled: loading, children: "Refresh" }), _jsx(Button, { variant: "contained", startIcon: _jsx(DownloadIcon, {}), onClick: handleExport, disabled: exporting || serviceHistory.length === 0, children: exporting ? 'Exporting...' : 'Export to Excel' })] })] }), error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, onClose: () => setError(null), children: error })), (overdueCount > 0 || upcomingCount > 0) && (_jsxs(Alert, { severity: overdueCount > 0 ? 'error' : 'warning', sx: { mb: 2 }, children: [overdueCount > 0 && `${overdueCount} machine(s) overdue for service. `, upcomingCount > 0 && `${upcomingCount} service(s) due within 30 days.`] })), _jsx(Alert, { severity: "info", sx: { mb: 2 }, children: "Complete service history across all machines and businesses. Sort by \"Next Service\" or \"Days Until\" to identify overdue and upcoming services. Red = overdue, Orange = due within 30 days, Green = future." }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 4 }, children: _jsx(CircularProgress, {}) })) : (_jsx(Box, { sx: { height: 600, width: '100%' }, children: _jsx(DataGrid, { rows: serviceHistory, columns: columns, pageSizeOptions: [25, 50, 100], initialState: {
                        pagination: {
                            paginationModel: { pageSize: 25 },
                        },
                        sorting: {
                            sortModel: [{ field: 'nextServiceDate', sort: 'asc' }],
                        },
                    }, slots: { toolbar: GridToolbar }, slotProps: {
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }, disableRowSelectionOnClick: true }) })), _jsxs(Box, { sx: { mt: 2, display: 'flex', gap: 3 }, children: [_jsxs(Typography, { variant: "caption", color: "text.secondary", children: ["Total Records: ", serviceHistory.length] }), overdueCount > 0 && (_jsxs(Typography, { variant: "caption", color: "error.main", children: ["Overdue: ", overdueCount] })), upcomingCount > 0 && (_jsxs(Typography, { variant: "caption", color: "warning.main", children: ["Due Soon: ", upcomingCount] }))] })] }));
};
export default AdminServiceHistory;
